/** /t/settings — what this computer syncs with and who it works as (Teacher Computer app).
 * The sidecar owns the cloud origin and the cloud sign-in (host_settings, host_credentials);
 * the web keeps only the local host token and asks the sidecar to change things. On the
 * cloud build there is nothing to set, and the page says so. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client";
import { capabilities } from "../platform/capabilities";
import { product } from "../product";
import { TeacherChrome } from "../teacher/Chrome";
import { PairingCard } from "../teacher/PairingCard";
import { getContentState, getHostInfo, getSyncStatus, hostLogin, hostLogout, publishContent, setCloud, setRoomName, type HostInfo, type SyncStatus } from "../teacher/api";
import { Button, PasswordInput, StatusBar, type ConnState } from "../ui";

const card = "rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm";

/** Three situations, never "synced" without a target: no server; a server that does not
 * answer (or an upload that failed); a server that answers, with or without a backlog. */
function syncState(host: HostInfo, sync: SyncStatus | undefined): ConnState {
  if (!host.cloud || !sync?.target) return "offline";
  if (host.online === false) return "offline";
  if (sync.last_error) return "reconnecting";
  return sync.pending_sessions > 0 ? "syncing" : "synced";
}

export function Settings() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const host = useQuery({ queryKey: ["host-info"], queryFn: getHostInfo, retry: false, refetchInterval: 10_000 });
  const sync = useQuery({ queryKey: ["sync-status"], queryFn: getSyncStatus, retry: false, refetchInterval: 10_000, enabled: !!host.data?.local });
  const invalidate = (...keys: string[]) => keys.forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));

  // The field shows what the teacher typed, else what the sidecar has; null = untouched.
  const [typed, setTyped] = useState<string | null>(null);
  const url = typed ?? host.data?.cloud ?? "";
  const [cloudNote, setCloudNote] = useState<"ok" | "removed" | null>(null);
  // STORY-111: what phones see when they find this computer on the Wi-Fi
  const [roomTyped, setRoomTyped] = useState<string | null>(null);
  const room = useMutation({
    mutationFn: (name: string) => setRoomName(name),
    onSuccess: () => {
      setRoomTyped(null);
      invalidate("host-info");
    },
  });
  const cloud = useMutation({
    mutationFn: (u: string) => setCloud(u),
    onSuccess: (r) => {
      setCloudNote(r.cloud ? "ok" : "removed");
      setTyped(null);
      invalidate("host-info", "host-content", "sync-status", "courses", "host-cache");
    },
  });
  const cloudError = (e: unknown, tried: string) => {
    const code = e instanceof ApiError ? e.code : undefined;
    if (code === "invalid_url") return t("settings.cloudInvalid");
    if (code === "not_ours") return t("settings.cloudNotOurs", { url: tried, product: product.name });
    return t("settings.cloudUnreachable", { url: tried });
  };

  // STORY-112: this computer plans courses in its own database; publishing sends them up.
  // Its own query: the counts and the last sync error are teacher-only, unlike host info.
  const contentQuery = useQuery({ queryKey: ["host-content"], queryFn: getContentState, retry: false, refetchInterval: 10_000, enabled: !!host.data?.local_content });
  const publish = useMutation({
    mutationFn: () => publishContent(),
    onSuccess: () => invalidate("host-info", "host-content", "courses"),
  });

  const [id, setId] = useState("");
  const [pw, setPw] = useState("");
  const [accountNote, setAccountNote] = useState<"signedOut" | null>(null);
  const login = useMutation({
    mutationFn: () => hostLogin(id, pw),
    onSuccess: () => {
      setId("");
      setPw("");
      setAccountNote(null);
      invalidate("host-info", "host-content", "courses", "reach");
    },
  });
  const logout = useMutation({
    mutationFn: () => hostLogout(),
    onSuccess: () => {
      setAccountNote("signedOut");
      invalidate("host-info", "host-content", "courses");
    },
  });

  const h = host.data;
  const content = contentQuery.data ?? null;
  /** "3 minutes ago" in the teacher's language; the exact time is in the status card above. */
  const since = (iso: string) => {
    const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
    const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" });
    if (Math.abs(minutes) < 60) return rtf.format(-minutes, "minute");
    const hours = Math.round(minutes / 60);
    return Math.abs(hours) < 24 ? rtf.format(-hours, "hour") : rtf.format(-Math.round(hours / 24), "day");
  };
  const state = h ? syncState(h, sync.data) : "offline";
  const detail = !h
    ? undefined
    : state === "offline" || state === "reconnecting"
      ? (h.cloud ?? t("settings.cloudNone"))
      : state === "syncing"
        ? t("teacher.pendingUpload", { count: sync.data?.pending_sessions ?? 0, events: sync.data?.pending_events ?? 0 })
        : t("settings.nothingPending");

  return (
    <TeacherChrome title={t("settings.title")}>
      <header>
        <p className="text-sm text-[color:var(--ink-2)]"><Link to="/t" className="underline">{t("host.toCourses")}</Link></p>
        <p className="mt-1 max-w-prose text-sm text-[color:var(--ink-2)]">{t("settings.lede")}</p>
      </header>
      {host.isPending && <p>{t("app.loading")}</p>}
      {host.isError &&
        (capabilities.desktop ? (
          // the shell is here but its local service is not answering (starting, restarting, or a rejected host token)
          <section className={card}>
            <p role="alert" className="text-sm">{t("settings.hostDown")}</p>
            <div className="mt-3">
              <Button variant="secondary" loading={host.isFetching} onClick={() => void host.refetch()}>{t("settings.hostRetry")}</Button>
            </div>
          </section>
        ) : (
          <p className={card}>{t("settings.onlyDesktop")}</p>
        ))}
      {h && (
        <div className="flex flex-col gap-4">
          <section className={card}>
            <h2 className="mb-2 font-semibold">{t("settings.status")}</h2>
            <StatusBar state={state} detail={detail} />
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
              <dt className="text-[color:var(--ink-2)]">{t("settings.deviceId")}</dt>
              <dd className="font-mono text-xs" translate="no">{h.device_id}</dd>
            </dl>
            <p className="mt-1 text-sm text-[color:var(--ink-2)]">{h.registered ? t("settings.registered") : t("settings.notRegistered")}</p>
            {h.wiped && <p role="alert" className="mt-1 text-sm text-[color:var(--danger)]">{t("settings.wiped")}</p>}
            {sync.data?.last_error && <p className="mt-1 text-xs text-[color:var(--not-yet)]">{t("settings.lastError", { error: sync.data.last_error })}</p>}
            {sync.data && (
              <p className="mt-1 text-xs text-[color:var(--ink-2)]">
                {t("settings.checkedAt", { at: new Date(sync.data.checked_at).toLocaleTimeString(i18n.language, { timeStyle: "short" }) })}
              </p>
            )}
          </section>

          <section className={card}>
            <h2 className="font-semibold">{t("settings.room")}</h2>
            <p className="mb-3 text-sm text-[color:var(--ink-2)]">{t("settings.roomBody")}</p>
            <form
              className="flex flex-wrap items-end gap-2"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                room.mutate((roomTyped ?? h.room ?? "").trim());
              }}
            >
              <label className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="text-sm font-medium">{t("settings.roomName")}</span>
                <input className="field" name="room" maxLength={60} value={roomTyped ?? h.room ?? ""} onChange={(e) => setRoomTyped(e.target.value)} />
              </label>
              <Button type="submit" variant="secondary" loading={room.isPending} disabled={roomTyped === null}>
                {t("settings.roomSave")}
              </Button>
            </form>
          </section>

          <section className={card}>
            <h2 className="font-semibold">{t("settings.cloud")}</h2>
            <p className="mb-3 text-sm text-[color:var(--ink-2)]">{t("settings.cloudBody")}</p>
            <form
              className="flex flex-col gap-3"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                setCloudNote(null);
                cloud.mutate(url.trim());
              }}
            >
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium">{t("settings.cloudUrl")}</span>
                <input
                  className="field"
                  name="cloud"
                  type="url"
                  inputMode="url"
                  spellCheck={false}
                  autoCapitalize="none"
                  autoComplete="url"
                  placeholder={t("settings.cloudPlaceholder")}
                  value={url}
                  onChange={(e) => setTyped(e.target.value)}
                />
                <span className="text-xs text-[color:var(--ink-2)]">{t("settings.cloudHint")}</span>
              </label>
              <div className="flex flex-wrap gap-2">
                <Button type="submit" loading={cloud.isPending && cloud.variables !== ""}>{t("settings.cloudCheck")}</Button>
                {h.cloud && (
                  <Button
                    variant="tertiary"
                    loading={cloud.isPending && cloud.variables === ""}
                    onClick={() => {
                      setCloudNote(null);
                      cloud.mutate("");
                    }}
                  >
                    {t("settings.cloudRemove")}
                  </Button>
                )}
              </div>
              {cloud.isError && <p role="alert" className="text-sm text-[color:var(--danger)]">{cloudError(cloud.error, cloud.variables ?? "")}</p>}
              {cloudNote && !cloud.isError && (
                <p role="status" className="text-sm">{cloudNote === "ok" ? t("settings.cloudOk") : t("settings.cloudRemoved")}</p>
              )}
            </form>
          </section>

          {h.local_content && (
            <section className={card}>
              <h2 className="font-semibold">{t("settings.contentLocalTitle")}</h2>
              {!h.cloud && <p className="mb-3 text-sm text-[color:var(--ink-2)]">{t("settings.contentLocalBody")}</p>}
              {h.signed_in && content && (
                <p className="text-sm">
                  {content.published > 0
                    ? t("settings.contentPublished", { published: content.published, total: content.total })
                    : t("settings.contentNever")}
                </p>
              )}
              {h.signed_in && content?.last_sync && (
                <p className="mt-1 text-xs text-[color:var(--ink-2)]">{t("settings.lastSync", { when: since(content.last_sync) })}</p>
              )}
              <div className="mt-3">
                <Button variant="secondary" aria-describedby={h.signed_in ? undefined : "publish-hint"} disabled={!h.signed_in} loading={publish.isPending} onClick={() => publish.mutate()}>
                  {publish.isPending ? t("settings.publishing") : t("settings.publish")}
                </Button>
              </div>
              {!h.signed_in && <p id="publish-hint" className="mt-2 text-xs text-[color:var(--ink-2)]">{h.cloud ? t("settings.publishNeedsSignIn") : t("settings.needCloud")}</p>}
              {(publish.isError || content?.error) && <p role="alert" className="mt-2 text-sm text-[color:var(--danger)]">{t("settings.publishFailed")}</p>}
              <p className="mt-2 text-xs text-[color:var(--ink-2)]">{t("settings.contentPartial")}</p>
            </section>
          )}

          <section className={card}>
            <h2 className="font-semibold">{t("settings.account")}</h2>
            <p className="mb-3 text-sm text-[color:var(--ink-2)]">{t("settings.accountBody")}</p>
            {h.signed_in ? (
              <div className="flex flex-col gap-3">
                <p>{t("settings.signedInAs", { name: h.account?.username ?? h.account?.email ?? "" })}</p>
                <div>
                  <Button variant="secondary" loading={logout.isPending} onClick={() => logout.mutate()}>{t("app.signOut")}</Button>
                </div>
              </div>
            ) : (
              <form
                className="flex flex-col gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  login.mutate();
                }}
              >
                <fieldset disabled={!h.cloud} className="flex flex-col gap-3 disabled:opacity-60">
                  <label className="flex flex-col gap-1">
                    <span className="text-sm font-medium">{t("signin.identifier")}</span>
                    <input className="field py-3" name="identifier" value={id} onChange={(e) => setId(e.target.value)} autoComplete="username" spellCheck={false} autoCapitalize="none" required />
                  </label>
                  <PasswordInput label={t("signin.password")} name="password" value={pw} onChange={setPw} autoComplete="current-password" required />
                  <div>
                    <Button type="submit" loading={login.isPending}>{t("signin.cta")}</Button>
                  </div>
                </fieldset>
                {!h.cloud && <p className="text-xs text-[color:var(--ink-2)]">{t("settings.needCloud")}</p>}
                {login.isError && <p role="alert" className="text-sm text-[color:var(--danger)]">{t("settings.signInFailed")}</p>}
                {accountNote === "signedOut" && !login.isError && <p role="status" className="text-sm">{t("settings.signedOut")}</p>}
              </form>
            )}
          </section>

          <section className={card}>
            <h2 className="font-semibold">{t("settings.phone")}</h2>
            <p className="mb-2 text-sm text-[color:var(--ink-2)]">{t("settings.phoneBody")}</p>
            <PairingCard bare />
            <p className="mt-2 flex flex-wrap gap-4 text-sm">
              <Link to="/t/host" className="underline">{t("host.open")}</Link>
              <Link to="/t/devices" className="underline">{t("devices.link")}</Link>
            </p>
          </section>
        </div>
      )}
    </TeacherChrome>
  );
}
